import { create } from "zustand";

type SupplierOrderTab =
  | "CURRENT"
  | "DELIVERED"
  | "RETURNED"
  | "CANCELLED";

interface ReviewEntity {
  entityType: "shipment" | "shipment_item";
  shipmentId?: number;
  shipmentItemId?: number;
  orderId?: number;
  info?: any;
}

interface SupplierOrderFilters {
  fulfillmentStatus: string;
  dateFrom: string;
  dateTo: string;
  minAmount: string;
  maxAmount: string;
  shipmentStatus: string;
  deliveryMethod: string;
  search: string;
}

interface SupplierOrderState extends SupplierOrderFilters {
  activeTab: SupplierOrderTab;

  page: number;
  pageSize: number;

  reviewEntity: ReviewEntity | null;
  reviewData: any | null;
  isReviewViewModalOpen: boolean;

  setActiveTab: (tab: SupplierOrderTab) => void;
  setFilter: (key: keyof SupplierOrderFilters, value: string) => void;
  setPage: (page: number) => void;
  resetFilters: () => void;
  getFilters: () => SupplierOrderFilters;

  openViewReview: (entity: ReviewEntity, review: any) => void;
  closeReviewModal: () => void;
}

const initialFilters: SupplierOrderFilters = {
  fulfillmentStatus: "",
  dateFrom: "",
  dateTo: "",
  minAmount: "",
  maxAmount: "",
  shipmentStatus: "",
  deliveryMethod: "",
  search: "",
};

export const useSupplierOrderStore = create<SupplierOrderState>((set, get) => ({
  activeTab: "CURRENT",

  page: 1,
  pageSize: 10,

  ...initialFilters,

  reviewEntity: null,
  reviewData: null,
  isReviewViewModalOpen: false,

  setActiveTab: (tab) =>
    set((state) => {
      if (state.activeTab === tab) {
        return state;
      }

      return {
        activeTab: tab,
        page: 1,
        shipmentStatus: "",
      };
    }),

  setFilter: (key, value) =>
    set((state) => {
      if (state[key] === value) {
        return state;
      }

      return {
        [key]: value,
        page: 1,
      } as Partial<SupplierOrderState>;
    }),

  setPage: (page) =>
    set((state) => {
      if (state.page === page) {
        return state;
      }

      return {
        page,
      };
    }),

  resetFilters: () =>
    set((state) => {
      const filtersAreAlreadyEmpty =
        state.fulfillmentStatus === "" &&
        state.dateFrom === "" &&
        state.dateTo === "" &&
        state.minAmount === "" &&
        state.maxAmount === "" &&
        state.shipmentStatus === "" &&
        state.deliveryMethod === "" &&
        state.search === "";

      if (filtersAreAlreadyEmpty && state.page === 1) {
        return state;
      }

      return {
        ...initialFilters,
        page: 1,
      };
    }),

  getFilters: () => {
    const state = get();

    return {
      fulfillmentStatus: state.fulfillmentStatus,
      dateFrom: state.dateFrom,
      dateTo: state.dateTo,
      minAmount: state.minAmount,
      maxAmount: state.maxAmount,
      shipmentStatus: state.shipmentStatus,
      deliveryMethod: state.deliveryMethod,
      search: state.search,
    };
  },

  openViewReview: (entity, review) =>
    set({
      reviewEntity: entity,
      reviewData: review,
      isReviewViewModalOpen: true,
    }),

  closeReviewModal: () =>
    set({
      isReviewViewModalOpen: false,
      reviewEntity: null,
      reviewData: null,
    }),
}));
